/**
 * Keeping the screen on for the length of a meeting.
 *
 * A phone locks after its screen timeout (15 s on some) unless the page asks it
 * not to, and the meeting never asked. It only stayed on while our camera was
 * on, because the browser keeps the screen awake for a camera that is
 * capturing. Turn the camera off, mic on or not, and nothing else counted:
 * every video tile is muted and remote audio plays through hidden <audio>
 * elements, which the browser treats as background sound rather than something
 * being watched. So the screen went dark mid-call, the page was hidden, and the
 * phone carried on in the meeting locked.
 *
 * The Screen Wake Lock API asks for the screen to stay on, with rules of its own:
 *
 *  - The browser drops the lock whenever the page is hidden (screen off, app
 *    switch, notification shade) and never takes it back by itself. So it is
 *    requested again each time the page comes back into view.
 *  - It may also drop it while the page is in view; the spec allows that at any
 *    time. Asked for again `RE_REQUEST_AFTER_RELEASE_MS` later.
 *  - A request from a hidden page, or from a frame whose host did not grant
 *    `screen-wake-lock` (see EMBED_IFRAME_ALLOW), is refused. A refusal is
 *    reported and left until the page next comes into view, so a frame that
 *    will never be allowed does not ask in a loop.
 *
 * Held on every device, not only phones: a laptop in an audio-only call dims
 * and locks the same way. Browsers without the API (iOS Safari before 16.4,
 * Chrome before 84, Firefox before 126, any page served over plain http) get
 * nothing, and the screen behaves as it always did.
 */
import type { VisibilitySource } from './camera-interruption'

/** The slice of a `WakeLockSentinel` this uses, declared structurally for the tests. */
export type WakeLockSentinelLike = {
  readonly released: boolean
  release(): Promise<void>
  addEventListener(type: 'release', listener: () => void): void
  removeEventListener(type: 'release', listener: () => void): void
}

/** The slice of `navigator.wakeLock` this uses. */
export type WakeLockLike = {
  request(type: 'screen'): Promise<WakeLockSentinelLike>
}

export type ScreenWakeLockEvent =
  | { type: 'unsupported' }
  | { type: 'acquired' }
  /** The browser let go of it. `visible` says whether the page was still in view. */
  | { type: 'released'; visible: boolean }
  | { type: 'refused'; error: string }

export type ScreenWakeLockOptions = {
  /** Defaults to `navigator.wakeLock`; `null` is a browser without the API. */
  wakeLock?: WakeLockLike | null
  doc?: VisibilitySource | null
  onEvent?: (event: ScreenWakeLockEvent) => void
  setTimeoutFn?: (fn: () => void, ms: number) => unknown
  clearTimeoutFn?: (handle: unknown) => void
}

export type ScreenWakeLock = {
  /** Whether the screen is being kept on right now. */
  readonly held: boolean
  dispose(): void
}

export const RE_REQUEST_AFTER_RELEASE_MS = 1_000

function browserWakeLock(): WakeLockLike | null {
  if (typeof navigator === 'undefined' || !('wakeLock' in navigator) || !navigator.wakeLock) return null
  return navigator.wakeLock
}

function errorText(err: unknown): string {
  if (err && typeof err === 'object' && 'name' in err) {
    const { name, message } = err as { name: unknown; message?: unknown }
    return message ? `${String(name)}: ${String(message)}` : String(name)
  }
  return String(err)
}

export function createScreenWakeLock(opts: ScreenWakeLockOptions = {}): ScreenWakeLock {
  const wakeLock = opts.wakeLock !== undefined ? opts.wakeLock : browserWakeLock()
  const doc = opts.doc !== undefined ? opts.doc : typeof document === 'undefined' ? null : document
  const setT = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms))
  const clearT = opts.clearTimeoutFn ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const report = (event: ScreenWakeLockEvent) => opts.onEvent?.(event)

  if (!wakeLock) {
    report({ type: 'unsupported' })
    return { held: false, dispose() {} }
  }

  let sentinel: WakeLockSentinelLike | null = null
  let pending = false
  // The page came back into view while a request was still out. That request
  // may have been made, and refused, while the page was hidden, so it is asked
  // for once more when it settles without a lock.
  let askAgain = false
  let retryTimer: unknown = null
  let disposed = false

  const visible = () => !doc || doc.visibilityState === 'visible'

  const clearRetry = () => {
    if (retryTimer === null) return
    clearT(retryTimer)
    retryTimer = null
  }

  const onRelease = () => {
    sentinel?.removeEventListener('release', onRelease)
    sentinel = null
    if (disposed) return
    const stillVisible = visible()
    report({ type: 'released', visible: stillVisible })
    // Hidden: the visibility handler asks again on the way back. In view: the
    // browser chose to let go, so ask again shortly rather than in a tight loop.
    if (stillVisible) {
      clearRetry()
      retryTimer = setT(() => {
        retryTimer = null
        acquire()
      }, RE_REQUEST_AFTER_RELEASE_MS)
    }
  }

  const acquire = () => {
    if (disposed || sentinel || !visible()) return
    if (pending) {
      askAgain = true
      return
    }
    pending = true
    askAgain = false
    wakeLock.request('screen').then(
      (next) => {
        pending = false
        if (disposed) {
          void next.release().catch(() => {})
          return
        }
        // Released before it reached us: the page was hidden in the meantime,
        // and coming back into view asks again.
        if (!next.released) {
          sentinel = next
          next.addEventListener('release', onRelease)
          report({ type: 'acquired' })
        }
        if (askAgain) acquire()
      },
      (err: unknown) => {
        pending = false
        if (disposed) return
        report({ type: 'refused', error: errorText(err) })
        if (askAgain) acquire()
      },
    )
  }

  const onVisibility = () => {
    if (visible()) acquire()
    else clearRetry()
  }

  doc?.addEventListener('visibilitychange', onVisibility)
  acquire()

  return {
    get held() {
      return sentinel !== null && !sentinel.released
    },
    dispose() {
      if (disposed) return
      disposed = true
      clearRetry()
      doc?.removeEventListener('visibilitychange', onVisibility)
      const held = sentinel
      sentinel = null
      if (held) {
        held.removeEventListener('release', onRelease)
        void held.release().catch(() => {})
      }
    },
  }
}
